import { calculateMortgageExpenses } from "../mortgage/mortgage";
import { sum } from "../../../utils";
const MONTHS_IN_YEAR = 12;
/**
 * Calculates monthly housing expenses
 *
 * @param input Forecast input for swedish market
 * @param amortizationRequirementHistory Relevant amortization rate history,
 * loan amount at rate change and highest adults income so far
 * @param localizationParameters Swedish mortgage market regulated parameters
 * @returns Calculated monthly housing expenses
 */
export function calculateHousingExpenses(input, amortizationRequirementHistory, localizationParameters) {
    var _a, _b, _c, _d, _e, _f;
    const { totalMonthlyAmortization, totalMonthlyInterestBeforeDeduction, monthlyAmortization, additionalMortgageAmortization, additionalMortgageInterest, loanToValueAmortizationRate, debtToIncomeAmortizationRate, } = calculateMortgageExpenses(input, amortizationRequirementHistory, localizationParameters);
    const fee = (_a = input.monthlyFee) !== null && _a !== void 0 ? _a : 0;
    const propertyTax = Math.round(input.propertyTax / MONTHS_IN_YEAR);
    const housingExpensesBeforeTaxDeduction = totalMonthlyInterestBeforeDeduction + totalMonthlyAmortization + input.monthlyMaintenance + propertyTax + fee;
    const additionalPropertyTax = Math.round((((_b = input.additionalHousing) === null || _b === void 0 ? void 0 : _b.propertyTax) || 0) / MONTHS_IN_YEAR);
    return {
        totalMonthlyAmortization,
        totalMonthlyInterestBeforeDeduction,
        housingExpensesBeforeTaxDeduction,
        loanToValueAmortizationRate,
        debtToIncomeAmortizationRate,
        monthlyAmortization,
        maintenance: input.monthlyMaintenance,
        fee,
        propertyTax,
        additionalHousingExpenses: {
            amortization: additionalMortgageAmortization !== null && additionalMortgageAmortization !== void 0 ? additionalMortgageAmortization : 0,
            interestBeforeDeduction: additionalMortgageInterest !== null && additionalMortgageInterest !== void 0 ? additionalMortgageInterest : 0,
            maintenance: (_d = (_c = input.additionalHousing) === null || _c === void 0 ? void 0 : _c.maintenance) !== null && _d !== void 0 ? _d : 0,
            fee: (_f = (_e = input.additionalHousing) === null || _e === void 0 ? void 0 : _e.fee) !== null && _f !== void 0 ? _f : 0,
            propertyTax: additionalPropertyTax,
        },
    };
}
/**
 * Sums monthly housing expenses
 *
 * @param housingExpenses Calculated monthly housing expenses
 * @returns Sum of all monthly housing expenses
 */
export function sumHousingExpenses(housingExpenses) {
    return (housingExpenses.totalMonthlyAmortization +
        housingExpenses.totalMonthlyInterestBeforeDeduction +
        housingExpenses.maintenance +
        housingExpenses.propertyTax +
        housingExpenses.fee);
}
/**
 * Sums all expenses by additional housing
 *
 * @param additionalHousingExpenses amortization, interest, maintenance, fee and property tax
 * @returns Sum of all additional housing expenses
 */
export function sumAdditionalHousingExpenses(additionalHousingExpenses) {
    const expenses = sum(Object.values(additionalHousingExpenses));
    return expenses;
}
/**
 * Calculates property tax depending on housing price.
 * Calculations for property tax are possible to overrule by declaring useMaxPropertyTax as true.
 * Then the return value will always be max property tax.
 *
 * @param maxPropertyTax Max property tax due to market regulations
 * @param housingPrice Estimated housing price by user
 * @param useMaxPropertyTax Boolean for returning max property tax
 * @returns Calculated property tax
 */
export function calculatePropertyTax(maxPropertyTax, housingPrice, useMaxPropertyTax) {
    if (useMaxPropertyTax) {
        return maxPropertyTax;
    }
    const housingTaxationFactor = 0.75;
    const propertyTaxationFactor = 0.0075;
    let propertyTax = Math.ceil(housingPrice * housingTaxationFactor * propertyTaxationFactor);
    propertyTax = Math.min(propertyTax, maxPropertyTax);
    return propertyTax;
}
